import React, { useEffect, useState } from 'react'
import { Box, Paper, Grid, Button, TextField, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import MomentUtils from '@date-io/moment'
import moment from 'moment'
import DataTable from 'src/components/common/DataTables/DataTable'
import { useLazyGetOtoTransactionsDiscrepanciesQuery } from 'src/_api/mapitShipApi'
import { useTranslation } from 'react-i18next'
import { MoreVert, OpenInNew } from '@mui/icons-material'
import { PopoverMenuList } from 'src/_modules/common'
import MapitShipOrderStatus from 'src/_modules/MapitShip/views/MapitShipOrders/components/MapitShipOrderStatus'

export default function OTOTransactionsDiscrepancies() {
    const { t } = useTranslation()

    const [params, setParams] = useState({ fromDate: null, toDate: null })
    const [hasSearched, setHasSearched] = useState(false)

    const [getOtoTransactions, { data, isLoading, isFetching }] = useLazyGetOtoTransactionsDiscrepanciesQuery()

    const handleChange = ({ target: { name, value } }) => {
        setParams(prev => ({ ...prev, [name]: value }))
    }

    const formatPayload = (p) => {
        const payload = {}
        if (p.fromDate) payload.fromDate = moment(p.fromDate).toISOString()
        if (p.toDate) payload.toDate = moment(p.toDate).toISOString()
        return payload
    }

    const searchHandler = async () => {
        const payload = formatPayload({ ...params })
        await getOtoTransactions(payload)
        setHasSearched(true)
    }

    // ShipmentColumn component with individual state management
    const ShipmentColumnCell = ({ value, row }) => {
        const openShipmentDetails = (event) => {
            event.stopPropagation();
            if (value) {
                window.open(`/admin/orders/filter/mapit-ship?page=1&limit=20&status=ALL_ORDERS&shipmentNumber=${value}`, "_blank");
            }
        };

        const openTrackingUrl = (event) => {
            event.stopPropagation();
            if (row?.trackingUrl) {
                window.open(row.trackingUrl, "_blank", "noreferrer");
            }
        };

        if (!value) {
            return null;
        }

        return (
            <Box display="flex" alignItems="center" gap={1} width={"100%"}>
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {value}
                </Box>
                <PopoverMenuList
                    icon={<MoreVert />}
                    items={[
                        {
                            key: "view",
                            label: t("Open Shipment"),
                            icon: <OpenInNew fontSize="small" />,
                            onClick: openShipmentDetails,
                        },
                        {
                            key: "tracking",
                            label: t("Tracking"),
                            icon: <OpenInNew fontSize="small" />,
                            onClick: openTrackingUrl,
                            disabled: !row?.trackingUrl,
                        },
                    ]}
                />
            </Box>
        );
    };

    const columns = [
        {
            field: "shipmentId",
            renderCell: (params) => {
                return <ShipmentColumnCell {...params} />;
            },
            headerName: t('Shipment Ids'), width: 180
        },
        { field: 'orderNumber', headerName: t('Order No.'), width: 140 },
        {
            field: 'walletSum', headerName: t('Mapit Transactions Sum'), width: 180,
            valueGetter: ({ value }) => (value != null ? parseFloat(value).toFixed(2) : '-')
        },
        {
            field: "orderStatus",
            sortable: false,
            renderCell: (params) => (
                <MapitShipOrderStatus row={params.row} value={params.value} />
            ),
            headerName: t("Status"),
            width: 140,
        },
        {
            field: 'otoSum', headerName: t('OTO Transactions Sum'), width: 180,
            valueGetter: ({ value }) => (value != null ? parseFloat(value).toFixed(2) : '-')
        },
        {
            field: 'discrepancy', headerName: t('Discrepancy'), width: 140,
            valueGetter: ({ value }) => (value != null ? parseFloat(value).toFixed(2) : '-')
        }
    ]

    const [page, setPage] = useState(1);

    const onPageChange = (newPage) => {
        setPage(newPage);
    }

    return (
        <Box display="flex" flexDirection="column" gap={2}>

            <Typography variant="h3">{t("OTO Transactions Discrepancies")}</Typography>

            <Grid container spacing={2} alignItems="center">
                <LocalizationProvider dateAdapter={MomentUtils}>
                    <Grid item md={3} sm={6} xs={12}>
                        <DatePicker
                            inputFormat="DD/MM/YYYY"
                            label={t('From')}
                            value={params.fromDate}
                            onChange={(newValue) => handleChange({ target: { name: 'fromDate', value: newValue } })}
                            renderInput={(inputProps) => <TextField fullWidth size="small" {...inputProps} />}
                        />
                    </Grid>
                    <Grid item md={3} sm={6} xs={12}>
                        <DatePicker
                            inputFormat="DD/MM/YYYY"
                            label={t('To')}
                            value={params.toDate}
                            onChange={(newValue) => handleChange({ target: { name: 'toDate', value: newValue } })}
                            renderInput={(inputProps) => <TextField fullWidth size="small" {...inputProps} />}
                        />
                    </Grid>
                </LocalizationProvider>

                <Grid item xs sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" color="primary" onClick={searchHandler} disabled={isLoading || isFetching}>
                        {t('Search')}
                    </Button>
                </Grid>
            </Grid>

            <Paper sx={{ height: { xs: '75vh', md: '75vh' } }}>
                <DataTable
                    page={page}
                    rows={data ?? []}
                    columns={columns}
                    loading={isLoading || isFetching}
                    pageSize={20}
                    getRowId={(row) => row.shipmentId ?? `${row.orderNumber}-${Math.random()}`}
                    onPageChange={onPageChange}
                    checkboxSelection={false}
                    rowCount={data?.length || 0}
                    customComponents={["Pagination"]}
                    paginationMode="client"
                />
            </Paper>
        </Box>
    )
}
